"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemo, useRef } from "react";
import { scaleBand, scaleLinear } from "@visx/scale";
import { Bar } from "@visx/shape";
import { useContainerSize } from "@/hooks/useContainerSize";
import { cn } from "@/lib/utils";
import styles from "./mini-bar-chart.module.scss";
const CHART_MAX_H = 183;
const MIN_FULL_WIDTH_DAY_COUNT = 14;

/** Figma: default / ±2 neighbor / ±1 neighbor / Active — glow spread + hover Active state */
const OPACITY_DEFAULT = 0.2;
const OPACITY_NEIGHBOR_OUTER = 0.3;
const OPACITY_NEIGHBOR_INNER = 0.4;
const OPACITY_ACTIVE = 1;
function barFillOpacity(idx: number, activeIdx: number): number {
  if (idx === activeIdx) return OPACITY_ACTIVE;
  const d = Math.abs(idx - activeIdx);
  if (d === 1) return OPACITY_NEIGHBOR_INNER;
  if (d === 2) return OPACITY_NEIGHBOR_OUTER;
  return OPACITY_DEFAULT;
}

/** Figma default: subdued gray bars (--on-surface/subdued-ish). */
const INACTIVE_BAR_FILL = "rgba(255,255,255,0.09)";
type MiniBarChartProps = {
  /** One metric value per calendar day (bar height scales to max in series). */
  values: number[];
  /** Controlled: which day index gets the glow (0 = first day of month). */
  activeDayIndex: number;
  onDayPointerEnter: (dayIndex: number) => void;
  /** When false, all bars render gray (monthly default view); hover still reports day index. */
  coloredBars?: boolean;
  /** When false, bars are colored but none is treated as selected (no glow/spread). */
  showSelection?: boolean;
  accentColor?: string;
  className?: string;
};

/**
 * Stream stats bars by day; glow + hover are driven by the parent so all metric cards stay in sync.
 */
export function MiniBarChart(t0) {
  const $ = _c(44);
  const {
    values,
    activeDayIndex,
    onDayPointerEnter,
    coloredBars: t1,
    showSelection: t2,
    accentColor: t3,
    className
  } = t0;
  const coloredBars = t1 === undefined ? true : t1;
  const showSelection = t2 === undefined ? true : t2;
  const accentColor = t3 === undefined ? "#CFD604" : t3;
  const barCount = values.length;
  let t4;
  if ($[0] !== values) {
    t4 = values.some(_temp);
    $[0] = values;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const hasData = t4;
  let t5;
  bb0: {
    if (!hasData) {
      let t6;
      if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
        t6 = [];
        $[2] = t6;
      } else {
        t6 = $[2];
      }
      t5 = t6;
      break bb0;
    }
    const maxV = Math.max(1, ...values);
    let t6;
    if ($[3] !== maxV || $[4] !== values) {
      let t7;
      if ($[6] !== maxV) {
        t7 = v_0 => Math.max(4, Math.round(v_0 / maxV * CHART_MAX_H));
        $[6] = maxV;
        $[7] = t7;
      } else {
        t7 = $[7];
      }
      t6 = values.map(t7);
      $[3] = maxV;
      $[4] = values;
      $[5] = t6;
    } else {
      t6 = $[5];
    }
    t5 = t6;
  }
  const heightsPx = t5;
  const safeActive = Math.max(0, Math.min(barCount ? barCount - 1 : 0, activeDayIndex));
  const containerRef = useRef(null);
  const {
    width,
    height
  } = useContainerSize(containerRef);
  const safeWidth = Math.max(1, width);
  const safeHeight = Math.max(1, height);
  let t6;
  bb1: {
    if (width <= 0 || height <= 0) {
      t6 = null;
      break bb1;
    }
    if (!hasData) {
      let t7;
      if ($[8] !== safeHeight || $[9] !== safeWidth) {
        t7 = <svg width={safeWidth} height={safeHeight} aria-hidden={true} />;
        $[8] = safeHeight;
        $[9] = safeWidth;
        $[10] = t7;
      } else {
        t7 = $[10];
      }
      t6 = t7;
      break bb1;
    }
    const maxBarPx = Math.max(1, ...heightsPx, 1);
    let t10;
    let t11;
    let t12;
    let t13;
    let t7;
    let t8;
    let t9;
    if ($[11] !== accentColor || $[12] !== barCount || $[13] !== coloredBars || $[14] !== heightsPx || $[15] !== maxBarPx || $[16] !== onDayPointerEnter || $[17] !== safeActive || $[18] !== safeHeight || $[19] !== safeWidth || $[20] !== showSelection) {
      const domain = heightsPx.map(_temp2);
      const chartFillRatio = barCount >= MIN_FULL_WIDTH_DAY_COUNT ? 1 : barCount / MIN_FULL_WIDTH_DAY_COUNT;
      const usedChartWidth = Math.max(1, safeWidth * chartFillRatio);
      const xScale = scaleBand({
        domain,
        range: [0, usedChartWidth],
        paddingInner: 0.08,
        paddingOuter: 0
      });
      const yScale = scaleLinear({
        domain: [0, maxBarPx],
        range: [safeHeight, 0]
      });
      const barWidth = Math.max(1, xScale.bandwidth());
      t7 = safeWidth;
      t8 = safeHeight;
      t9 = `0 0 ${safeWidth} ${safeHeight}`;
      t10 = "none";
      t11 = "img";
      t12 = "Stream stats by day";
      t13 = heightsPx.map((value, idx_0) => {
        const key = String(idx_0);
        const x = xScale(key) ?? 0;
        const y = yScale(value);
        const barHeight = Math.max(1, safeHeight - y);
        const isGlowBar = coloredBars && showSelection && idx_0 === safeActive;
        let fillOpacity = 1;
        if (coloredBars) {
          fillOpacity = showSelection ? barFillOpacity(idx_0, safeActive) : OPACITY_DEFAULT;
        }
        const fill = coloredBars ? accentColor : INACTIVE_BAR_FILL;
        return <Bar key={key} x={x} y={safeHeight - barHeight} width={barWidth} height={barHeight} rx={2} fill={fill} fillOpacity={fillOpacity} style={isGlowBar ? {
          filter: `drop-shadow(0px 0px 8.1px ${accentColor})`
        } : undefined} onPointerEnter={() => onDayPointerEnter(idx_0)} onPointerDown={() => onDayPointerEnter(idx_0)} className={styles.bar} />;
      });
      $[11] = accentColor;
      $[12] = barCount;
      $[13] = coloredBars;
      $[14] = heightsPx;
      $[15] = maxBarPx;
      $[16] = onDayPointerEnter;
      $[17] = safeActive;
      $[18] = safeHeight;
      $[19] = safeWidth;
      $[20] = showSelection;
      $[21] = t10;
      $[22] = t11;
      $[23] = t12;
      $[24] = t13;
      $[25] = t7;
      $[26] = t8;
      $[27] = t9;
    } else {
      t10 = $[21];
      t11 = $[22];
      t12 = $[23];
      t13 = $[24];
      t7 = $[25];
      t8 = $[26];
      t9 = $[27];
    }
    let t14;
    if ($[28] !== t10 || $[29] !== t11 || $[30] !== t12 || $[31] !== t13 || $[32] !== t7 || $[33] !== t8 || $[34] !== t9) {
      t14 = <svg width={t7} height={t8} viewBox={t9} preserveAspectRatio={t10} role={t11} aria-label={t12}>{t13}</svg>;
      $[28] = t10;
      $[29] = t11;
      $[30] = t12;
      $[31] = t13;
      $[32] = t7;
      $[33] = t8;
      $[34] = t9;
      $[35] = t14;
    } else {
      t14 = $[35];
    }
    t6 = t14;
  }
  const chart = t6;
  let t7;
  if ($[36] !== className) {
    t7 = cn(styles.root, className);
    $[36] = className;
    $[37] = t7;
  } else {
    t7 = $[37];
  }
  let t8;
  if ($[38] !== hasData) {
    t8 = !hasData && <div className={styles.emptyPattern} aria-hidden={true} />;
    $[38] = hasData;
    $[39] = t8;
  } else {
    t8 = $[39];
  }
  let t9;
  if ($[40] !== chart || $[41] !== t7 || $[42] !== t8) {
    t9 = <div ref={containerRef} className={t7}>{t8}{chart}</div>;
    $[40] = chart;
    $[41] = t7;
    $[42] = t8;
    $[43] = t9;
  } else {
    t9 = $[43];
  }
  return t9;
}
function _temp2(_, idx) {
  return String(idx);
}
function _temp(v) {
  return v > 0;
}